import type { BridgedServices } from "./shared";
import { installAutosizeParent } from "./autosize/parent";
import { installEventsParent } from "./events/parent";
import { installDocumentMetadataParent } from "./document-metadata/parent";
import { installGraffitiParent } from "./graffiti/parent";
import { installHistoryParent } from "./history/parent";
import { installNavigationParent } from "./navigation/parent";
import type { NavigableTransclude } from "./navigation/shared";
import { installResolutionParent } from "./resolution/parent";
import { preparePeripheralsFrame } from "./peripherals/features";
import { installPeripheralsParent } from "./peripherals/parent";
import { prepareGuardFrame } from "./graffiti/identity";

/** Create an installer that can be used to connect this document to a transcluded iframe */
export function createParentBridgeEndpointInstaller(
  bridgedServices: BridgedServices,
) {
  const { guardPath, resolve, createGraffiti, documentRoute, peripherals, autosize } = bridgedServices;
  const install = (
    host: NavigableTransclude,
    iframe: HTMLIFrameElement,
    onEvent: (event: CustomEvent<unknown>) => void,
  ) => {
    const events = installEventsParent(iframe, onEvent);
    const metadataBridge = installDocumentMetadataParent(events);
    const navigationBridge = installNavigationParent(
      host,
      events,
      documentRoute,
    );
    const stopHistoryBridge = installHistoryParent(host, events, documentRoute);
    const autosizeBridge = installAutosizeParent(host, iframe, events, autosize);
    const resolution = installResolutionParent(events, resolve);
    const graffitiBridge = installGraffitiParent(iframe, host, createGraffiti());
    const peripheralsBridge = installPeripheralsParent(iframe, host, peripherals);

    return {
      destroy() {
        navigationBridge.destroy();
        stopHistoryBridge();
        metadataBridge.destroy();
        autosizeBridge.destroy();
        resolution.destroy();
        events.destroy();
        void graffitiBridge.destroy();
        peripheralsBridge.destroy();
      },
      send: events.send,
      setRoute: navigationBridge.setRoute,
      setQuery: navigationBridge.setQuery,
    };
  };
  install.prepareFrame = (iframe: HTMLIFrameElement, host: HTMLElement) => {
    prepareGuardFrame(iframe, host, guardPath);
    return preparePeripheralsFrame(iframe, host, peripherals);
  };
  return install;
}

type InstalledBridgeEndpoints = ReturnType<typeof createParentBridgeEndpointInstaller>;
export type ParentBridgeEndpointInstaller = {
  (...args: Parameters<InstalledBridgeEndpoints>): ReturnType<InstalledBridgeEndpoints>;
  /** Finish bridge bootstrap before child scripts run. Does not install endpoints. */
  prepareFrame?(iframe: HTMLIFrameElement, host: HTMLElement): void | Promise<void>;
};
